/**
 * Shared Entra sign-in button used on LoginPage and AcceptInvitePage.
 * Single button — Entra's hosted UI shows all configured providers (Microsoft, Google, Email OTP).
 */

import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Shield, LogIn } from 'lucide-react';
import authService from '../../services/api/authService';

export const SignInButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[3]};
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  background: ${({ theme }) => theme.colors.primary.base};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.white};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.primary.dark};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  &:active {
    background: ${({ theme }) => theme.colors.primary.navy};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const HIPAADivider = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[6]};

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme.colors.borders.light};
  }
`;

export const HIPAAText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;

  svg {
    width: 14px;
    height: 14px;
    color: ${({ theme }) => theme.colors.status.success.text};
  }
`;

interface EntraSignInButtonProps {
  invitationToken?: string;
  label?: string;
}

/** Single "Sign In" button — redirects to Entra's hosted login page. */
export const EntraSignInButton = ({ invitationToken, label = 'Sign In' }: EntraSignInButtonProps) => {
  const location = useLocation();
  const returnTo = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;

  const handleSignIn = () => {
    const opts = {
      ...(invitationToken ? { invitationToken } : {}),
      ...(returnTo ? { returnTo } : {}),
    };
    window.location.assign(
      authService.entraRedirectUrl(Object.keys(opts).length ? opts : undefined),
    );
  };

  return (
    <>
      <SignInButton type="button" onClick={handleSignIn}>
        <LogIn />
        {label}
      </SignInButton>

      <HIPAADivider>
        <HIPAAText>
          <Shield />
          HIPAA Compliant &amp; Secure
        </HIPAAText>
      </HIPAADivider>
    </>
  );
};

/** @deprecated Use EntraSignInButton instead */
export const EntraProviderButtons = EntraSignInButton;
